import { setPref } from "@common/preferences";
import { readJson } from "@jkast90/kast-common/storage";

// Cheat presets for the Pokémon games, by the game code the ROM header
// gives (guides/detection.ts), as mGBA cheat files. mGBA reads a `# name`
// line as a new set and takes Game Boy GameShark, GBA CodeBreaker and
// GameShark/Action Replay codes as they are. The core can only add sets
// while a game runs, so a change reloads the game (useEmulator.setCheats).

export type CheatId = "candies" | "balls" | "money";

export interface CheatPreset {
  id: CheatId;
  name: string;
  /** What the player sees in the game. */
  effect: string;
  lines: string[];
}

const NAME: Record<CheatId, string> = { candies: "Rare Candies", balls: "Pokéballs", money: "Money" };

function preset(id: CheatId, effect: string, ...lines: string[]): CheatPreset {
  return { id, name: NAME[id], effect, lines };
}

// Checked against pret's symbol files for the USA releases. Game Boy codes
// write one byte each frame (01 value address-low address-high): the bag
// ones pin the pocket's first slot, which needs an item in it already.
// Gen 1 money is BCD, Gen 2 big-endian binary. Ruby and Sapphire keep
// their save block in one place, so CodeBreaker writes go straight in.
// Emerald, FireRed and LeafGreen move the block and XOR money with a key
// drawn from the RNG, so those go through the Pokémart instead: the first
// item for sale, the sale price (FR/LG) or the sell quantity (Emerald).
const hex = (n: number, width: number) => n.toString(16).toUpperCase().padStart(width, "0");
const gb = (address: number, value: number) => `01${hex(value, 2)}${hex(address & 0xff, 2)}${hex(address >> 8, 2)}`;

const PRESETS: Record<string, CheatPreset[]> = {
  "POKEMON RED": gen1(0xd31e, 0xd347),
  "POKEMON BLUE": gen1(0xd31e, 0xd347),
  "POKEMON YELLOW": gen1(0xd31d, 0xd346),
  POKEMON_GLDAAUE: gen2(0xd5b8, 0xd5fd, 0xd573),
  POKEMON_SLVAAXE: gen2(0xd5b8, 0xd5fd, 0xd573),
  PM_CRYSTAL: gen2(0xd893, 0xd8d8, 0xd84e),
  AXVE: rubySapphire(),
  AXPE: rubySapphire(),
  BPEE: [
    preset("candies", "The first item on any Pokémart's list buys as Rare Candy ($4,800)", "82005274 0044"),
    preset("balls", "The first item on any Pokémart's list buys as a free Master Ball", "82005274 0001"),
    preset(
      "money",
      "Selling one item worth $202 or more brings money to 999,999, and you keep the item",
      "83005E18 270F",
    ),
  ],
  BPRE: fireRedLeafGreen(),
  BPGE: fireRedLeafGreen(),
};

function gen1(bag: number, money: number): CheatPreset[] {
  return [
    preset("candies", "The first bag slot holds 99 Rare Candies", gb(bag, 0x28), gb(bag + 1, 99)),
    preset("balls", "The first bag slot holds 99 Master Balls", gb(bag, 0x01), gb(bag + 1, 99)),
    preset("money", "Money is 999,999", gb(money, 0x99), gb(money + 1, 0x99), gb(money + 2, 0x99)),
  ];
}

function gen2(items: number, balls: number, money: number): CheatPreset[] {
  return [
    preset("candies", "The Items pocket's first slot holds 99 Rare Candies", gb(items, 0x20), gb(items + 1, 99)),
    preset("balls", "The Balls pocket's first slot holds 99 Master Balls", gb(balls, 0x01), gb(balls + 1, 99)),
    preset("money", "Money is 999,999", gb(money, 0x0f), gb(money + 1, 0x42), gb(money + 2, 0x3f)),
  ];
}

function rubySapphire(): CheatPreset[] {
  return [
    preset("candies", "The Items pocket's first slot holds 99 Rare Candies", "82025C94 0044", "82025C96 0063"),
    preset("balls", "The Balls pocket's first slot holds 99 Master Balls", "82025D34 0001", "82025D36 0063"),
    preset("money", "Money is 999,999", "82025BC4 423F", "82025BC6 000F"),
  ];
}

function fireRedLeafGreen(): CheatPreset[] {
  return [
    preset("candies", "Every Pokémart's first item is a free Rare Candy", "82003884 0044", "8203993C 0000"),
    preset("balls", "Every Pokémart's first item is a free Master Ball", "82003884 0001"),
    preset("money", "Everything in Pokémarts is free", "8203993C 0000"),
  ];
}

const KEY = "pocket-play.cheats.";

// ROM hacks keep their base game's header code, so FireRed's codes would
// corrupt them.
const HACKS = /unbound|radical|rocket|glazed|gaia|prism|crystal clear|polished/i;

/** The presets for a game, in menu order; none for games without any. */
export function cheatPresets(gameCode: string | null, romName: string): CheatPreset[] {
  if (!gameCode || HACKS.test(romName)) return [];
  return PRESETS[gameCode] ?? [];
}

export function loadCheatChoice(gameCode: string): CheatId[] {
  return readJson<unknown[]>(KEY + gameCode, [], { isValid: Array.isArray }).filter(
    (id): id is CheatId => typeof id === "string" && id in NAME,
  );
}

export function saveCheatChoice(gameCode: string, ids: CheatId[]) {
  try {
    setPref(KEY + gameCode, ids.length ? JSON.stringify(ids) : null);
  } catch {
    // Kept for this game at least.
  }
}

/** The cheat file for the chosen presets, or null when none is chosen. */
export function cheatsFileText(presets: CheatPreset[], chosen: CheatId[]): string | null {
  const sets = presets.filter((p) => chosen.includes(p.id));
  if (!sets.length) return null;
  return sets.map((p) => [`# ${p.name}`, ...p.lines].join("\n")).join("\n") + "\n";
}
